import React, { useState, useRef, useEffect } from "react";


export interface EntityPill {
  id: string;
  name: string;
  type: string;
  channelId: string;
  channelName: string;
  channelType?: string;
  icon?: string;
  level?: number;
  feed?: {
    source: string[];
    origin: string[];
    assets: {
      type: string;
      high: string[];
      low: string[];
    };
  };
}

const ChannelLogo = ({ type, icon }: { type?: string; icon?: string }) => {
  if (!icon) return null;

  // If we have a custom icon URL or backend config icon
  if (icon.startsWith('http') || icon.startsWith('/')) {
    return <img src={icon} alt={type} className="w-3.5 h-3.5 mr-1.5 object-contain rounded-sm" />;
  }

  // Text/Emoji icon
  return <span className="mr-1.5 text-xs leading-none">{icon}</span>;
}

interface EntityPillsProps {
  entities: EntityPill[];
  onPillClick: (entityId: string, channelId: string) => void;
  onExpandedChange?: (expanded: boolean) => void;
}

export const EntityPills: React.FC<EntityPillsProps> = ({
  entities,
  onPillClick,
  onExpandedChange,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Check if container would be scrollable in collapsed state
  useEffect(() => {
    const checkScrollable = () => {
      if (containerRef.current) {
        // Temporarily apply collapsed styles to check scrollability
        const container = containerRef.current;
        const originalClasses = container.className;

        // Apply collapsed state styles to check if it would be scrollable
        container.className =
          "flex items-center gap-2 flex-nowrap overflow-x-auto scrollbar-hide";

        const { scrollWidth, clientWidth } = container;
        setIsScrollable(scrollWidth > clientWidth);

        // Restore original classes
        container.className = originalClasses;
      }
    };

    checkScrollable();
    window.addEventListener("resize", checkScrollable);
    return () => window.removeEventListener("resize", checkScrollable);
  }, [entities]);

  if (entities.length === 0) {
    return null;
  }

  const toggleExpanded = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    onExpandedChange?.(newExpanded);
  };

  return (
    <div
      className={`relative flex items-center gap-2 ${isExpanded ? "z-30" : ""}`}
    >
      {/* Expand/Collapse icon - always show when expanded, otherwise only when scrollable */}
      {(isExpanded || isScrollable) && (
        <button
          onClick={toggleExpanded}
          className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-accent-primary/10 hover:bg-accent-primary/20 transition-colors border border-accent-primary/20"
          title={isExpanded ? "Collapse" : "Expand"}
        >
          <span className={`w-4 h-4 text-accent-primary transition-transform ${isExpanded ? "rotate-180" : ""}`} style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_6ae6167c.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_6ae6167c.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
        </button>
      )}

      {/* Pills container wrapper */}
      <div className="relative flex-1 min-w-0 h-9">
        <div
          ref={containerRef}
          className={`flex items-center gap-2 transition-all duration-200 ${isExpanded
            ? "absolute bottom-0 left-0 right-0 flex-wrap bg-white dark:bg-neutral-900 p-3 rounded-xl shadow-xl border border-neutral-200/50 dark:border-neutral-700/50 z-30"
            : "flex-nowrap overflow-x-auto scrollbar-hide h-full"
            }`}
        >
          {entities.map((entity, index) => (
            <div
              key={`${entity.channelId}-${entity.id}`}
              className="inline-flex items-center px-3 py-1.5 bg-accent-primary/10 dark:bg-accent-primary/20 text-neutral-900 dark:text-neutral-100 rounded-full text-sm cursor-pointer hover:bg-accent-primary/20 dark:hover:bg-accent-primary/30 transition-all animate-pill-in border border-accent-primary/20 dark:border-accent-primary/30 flex-shrink-0 whitespace-nowrap"
              style={{
                animationDelay: `${index * 50}ms`,
              }}
              onClick={() => {
                // Collapse if expanded before proceeding
                if (isExpanded) {
                  setIsExpanded(false);
                  onExpandedChange?.(false);
                }
                onPillClick(entity.id, entity.channelId);
              }}
            >
              <ChannelLogo type={entity.channelType} icon={entity.icon} />
              {entity.feed?.assets && (entity.feed.assets.low?.length > 0 || entity.feed.assets.high?.length > 0) && (
                <div className="flex-shrink-0 w-5 h-5 mr-1.5 rounded-sm overflow-hidden bg-neutral-800/10 dark:bg-neutral-100/10 -ml-0.5 border border-white/20">
                  <img src={entity.feed.assets.low?.[0] || entity.feed.assets.high?.[0]} alt="" className="w-full h-full object-cover" />
                </div>
              )}
              <span className="font-medium truncate max-w-[200px]">
                {entity.name.length > 30
                  ? `${entity.name.substring(0, 30)}...`
                  : entity.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
